import { useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useOfferLineTemplates, type OfferLineTemplate } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";
import { cn } from "@/lib/utils";

const PHASE_OPTIONS_FULL: Array<{ value: OfferPhase; label: string }> = [
  { value: "pl", label: "Prosjektledelse" },
  { value: "preprod", label: "Preproduksjon" },
  { value: "production", label: "Produksjon" },
  { value: "postprod", label: "Postproduksjon" },
  { value: "stills", label: "Stills" },
  { value: "reise", label: "Reise" },
];

/**
 * VIB-701: Søkbar katalog-picker for offer_line_templates. Erstatter direkte
 * redigering av phase/cost_type/pricing_type/unit i admin-UI for mal-linjer.
 * Items grupperes etter phase; søk filtrerer på navn.
 */
export function CatalogProductPicker({
  value,
  onChange,
  placeholder = "Velg produkt fra katalogen…",
}: {
  value: string | null | undefined;
  onChange: (id: string) => void;
  placeholder?: string;
}) {
  const { data: catalog = [] } = useOfferLineTemplates();
  const [open, setOpen] = useState(false);
  const selected = catalog.find((t) => t.id === value);

  const grouped = useMemo(() => {
    const m: Record<string, OfferLineTemplate[]> = {};
    for (const t of catalog) {
      if (!m[t.phase]) m[t.phase] = [];
      m[t.phase].push(t);
    }
    return m;
  }, [catalog]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs justify-between font-normal w-full"
        >
          {selected ? (
            selected.name
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <SrIcon
            name="chevrons-up-down"
            className="ml-2 h-3 w-3 shrink-0 opacity-50"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Søk produkt…" className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>Ingen treff</CommandEmpty>
            {PHASE_OPTIONS_FULL.map((phase) => {
              const items = grouped[phase.value];
              if (!items?.length) return null;
              return (
                <CommandGroup key={phase.value} heading={phase.label}>
                  {items.map((t) => (
                    <CommandItem
                      key={t.id}
                      value={`${t.name} ${phase.label}`}
                      onSelect={() => {
                        onChange(t.id);
                        setOpen(false);
                      }}
                      className="text-xs"
                    >
                      <SrIcon
                        name="check"
                        className={cn(
                          "mr-2 h-3 w-3",
                          value === t.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex-1">{t.name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {t.cost_type === "external" ? "Ekstern" : "Intern"} · {t.default_unit}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
